<template>
	<div class="request-form-container">
		<ValidationObserver ref="form">
			<form @submit.prevent="submit">
				<div class="column">
					<input-area
						name="first-name"
						id="first-name"
						label="Name"
						tag="input"
						v-model="form.firstName.value"
						:disabled="form.firstName.disabled"
					/>
					<input-area
						name="last-name"
						id="last-name"
						label="Last Name"
						tag="input"
						v-model="form.lastName.value"
						:disabled="form.lastName.disabled"
					/>
					<input-area
						name="country"
						id="country"
						label="Country"
						tag="input"
						v-model="form.country.value"
						:disabled="form.country.disabled"
					/>
					<input-area
						name="email"
						id="email"
						label="E-Mail Address"
						tag="input"
						v-model="form.email.value"
						:disabled="form.email.disabled"
					/>
					<ValidationProvider name="company" rules="required|maxlength" v-slot="{errors}">
						<input-area
							name="company"
							id="company"
							label="Organization/Company *"
							tag="input"
							v-model="form.company.value"
							:disabled="form.company.disabled"
							:isVisible="true"
							:error="errors[0]"
						/>
					</ValidationProvider>
					<ValidationProvider name="job-position" rules="required|maxlength" ref="jobs" v-slot="{errors}">
						<md-field class="form-group" :class="{'md-invalid': errors[0]}">
							<dropdown-custom
								class="is-dropdown"
								id="job-position"
								:placeholder="'Job Position *'"
								:list-options="jobPositionOptions"
								@input="handleJobDropdown"
								:value="selectedJobPosition"
								:disabled="form.jobPosition.disabled"
							/>
							<span class="md-error">{{ errors[0] }}</span>
						</md-field>
					</ValidationProvider>
					<ValidationProvider
						name="phone"
						:rules="{required: false, phoneRequired: true, regex: /^\+\d{3,34}$/}"
						v-slot="{errors}"
					>
						<input-area
							name="phone"
							id="phone"
							label="Phone number *"
							tag="input"
							v-model="form.phone.value"
							:disabled="form.phone.disabled"
							:error="errors[0]"
							hint="Add a phone number, so we can reach you easily."
						/>
					</ValidationProvider>
					<ValidationProvider name="city" rules="required|maxlength" v-slot="{errors}">
						<input-area
							name="city"
							id="city"
							label="City *"
							tag="input"
							v-model="form.city.value"
							:disabled="form.city.disabled"
							:error="errors[0]"
						/>
					</ValidationProvider>
					<ValidationProvider
						class="state"
						name="state"
						ref="state"
						rules="required|maxlength"
						v-slot="{errors}"
						v-if="jwt.country === 'US' || jwt.country === 'CA'"
					>
						<md-field class="form-group state-field" :class="{'md-invalid': errors[0]}">
              <dropdown-custom
								class="is-dropdown"
								id="state"
								:placeholder="'State *'"
								:list-options="stateOptions"
								@input="handleStateDropdown($event)"
								:value="selectedState"
								:disabled="form.state.disabled"
							/>
							<span class="md-error">{{ errors[0] }}</span>
						</md-field>
					</ValidationProvider>
				</div>

				<div class="column"></div>
			</form>
		</ValidationObserver>
	</div>
</template>

<script>
import {ValidationProvider, ValidationObserver, extend} from 'vee-validate';
import {required, regex} from 'vee-validate/dist/rules';
import {mapState} from 'vuex';
import countryList from '@/locales/country-list.json';
import DropdownCustom from '@/components/dropdown-custom/DropdownCustom';

// Override the default message.
const phoneErrorMessage =
	'Please provide phone number in correct format, without spaces, containing country code. Example: +490123456';

extend('required', {
	...required,
	message: 'This field is required.',
});

extend('phoneRequired', {
	...required,
	message: phoneErrorMessage,
});

extend('regex', {
	...regex,
	message: phoneErrorMessage,
});

extend('maxlength', (value) => {
  let currentValue = null;
  if(typeof value === 'object') {
    const [ job ] = Object.values(value.value);
    currentValue = job;
  } else {
    currentValue = value;
  }
	if (currentValue.length <= 100) return true;
	return 'Max length is 100 characters.';
});

export default {
	name: 'RequestForm',
	props: {},
	components: {
		ValidationProvider,
		ValidationObserver,
    DropdownCustom,
	},
	data() {
		return {
			selectedJobPosition: null,
			selectedState: null,
			form: {
				firstName: {
					label: 'Name',
					value: null,
					disabled: true,
				},
				lastName: {
					label: 'Last Name',
					value: null,
					disabled: true,
				},
				email: {
					label: 'E-Mail Address',
					value: null,
					disabled: true,
				},
				country: {
					label: 'Country',
					value: null,
					disabled: true,
				},
				city: {
					label: 'City *',
					value: null,
					disabled: false,
				},
				state: {
					label: 'State / Province',
					value: null,
					disabled: false,
				},
				phone: {
					label: 'Phone number *',
					value: null,
					disabled: false,
				},
				company: {
					label: 'Organization/Company *',
					value: null,
					disabled: false,
				},
				jobPosition: {
					label: 'Job Position *',
					value: null,
					disabled: false,
				},
			},
		};
	},
	computed: {
    LANG() {
      return this.$store.state.LANG;
    },
		...mapState(['jwt', 'jobPositions', 'states']),
		jobPositionOptions() {
			const {job_positions} = this.jobPositions;
			return this.mapDropdownList(job_positions);
		},
		stateOptions() {
			const { states: { Canada, USA } } = this.states;
			if (this.jwt.country === 'US') {
				return this.mapDropdownList(USA);
			}
			if (this.jwt.country === 'CA') {
				return this.mapDropdownList(Canada);
			}
			return null;
		},
	},
	methods: {
		mapDropdownList(data) {
			return data.map((job, index) => ({
        key: job.key,
				children: [],
        value: {
          [this.LANG]: job.displayName,
        },
				disabled: job.disabled ? job.disabled : false,
				index: index,
			}));
		},
		setUserData() {
			const getLoginUserData = ([key]) => {
				if (!this.jwt[key]) return;
				if (key === 'country') {
					this.form[key].value = countryList.find(
						(country) => country.abbreviation === this.jwt[key],
					).country;
				} else {
					this.form[key].value = this.jwt[key];
				}
			};
			Object.entries(this.form).forEach(getLoginUserData);
			this.formChanged();
		},
		formChanged() {
			this.$emit('input', this.form);
		},
		async validateForm() {
			return this.$refs.form.validate().then((success) => success);
		},
		async getFormData() {
			return this.form;
		},
		handleStateDropdown(option) {
			this.selectedState = option;
			this.form.state.value = option.key;
		},
		handleJobDropdown(option) {
			this.selectedJobPosition = option;
			this.form.jobPosition.value = option.key;
		},
	},
	mounted() {
		this.setUserData();
	},
};
</script>

<style lang="scss" scoped>
.request-form-container {
	max-width: 815px;
	margin: 0 auto;
}
form {
	margin-top: 42px;
	@include flexRow;

	@media (max-width: $mobile) {
		flex-direction: column;
	}

	.column {
    .md-field {
      margin: 0;
			padding: 0;
      margin-bottom: 40px;
    }

		@media (min-width: $mobile + 1) {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
			> .md-field,
			> span {
				width: calc(50% - 12px);
			}
			> .state {
				margin-left: auto;
			}
		}
		@media (max-width: $mobile) {
			.md-invalid#phone {
				margin-bottom: 52px;
			}
			input.md-input {
				width: 100% !important;
			}
			.md-field {
				margin-bottom: 33px;
			}
		}
		.md-field {
			label {
				left: 18px;
				top: 15px;
				z-index: 1;
			}
			&.md-has-value {
				color: $color-text-grey;
				label {
					color: $color-blue;
					top: -8px;
					padding: 0 4px;
					background-color: $white;
				}
			}
			&.md-focused,
			&.md-focused.md-has-value {
				label {
					top: -8px;
					padding: 0 4px;
					background-color: $white;
					color: $color-text-grey;
				}
			}
		}
		.md-field.md-disabled {
			label {
				color: $color-mid-grey;
			}
			.md-input {
				border: 1px solid $color-mid-grey;
				-webkit-text-fill-color: $color-mid-grey;
				-webkit-opacity: 1;
				color: $color-mid-grey;

				cursor: not-allowed;
			}
		}
		.md-helper-text {
			color: $color-grey;
		}
		.md-helper-text,
		.md-error {
			font-family: 'Futura W01 Book', Arial, Helvetica, sans-serif !important;
			font-size: 12px !important;
			padding-left: 18px;
		}

		.md-field {
			&.md-invalid,
			&.md-invalid.md-focused {
				color: $color-red;
				label {
					color: $color-red;
				}
				.md-input,
				&:focus {
					border-color: $color-red;
				}
			}
		}
	}
	.is-dropdown {
		width: 100%;
	}
}
.request-form-container .form-group.md-invalid {
	::v-deep button.dropdown_opener {
		border-color: $color-red;
	}
}
</style>
